/**
 * Additional service options — verbatim list from
 * `src/features/create-event/_components/sections/OtherDetails.tsx` so
 * customers see consistent choices between the long form and the new
 * details page during the deprecation window.
 *
 * Shared (not owned by either feature folder) because the create-event-v3 wizard and
 * `/details`'s Budget & decision section both read/write `events.additional_services` and
 * must agree on the same allowlist — see {@link filterAdditionalServices}, which is the
 * single place that allowlist is enforced.
 */
export const ADDITIONAL_SERVICE_OPTIONS = [
  { value: "Onsite Support", label: "Onsite support" },
  { value: "Flight Bookings", label: "Flight bookings" },
  { value: "Ground Transport Arrangements", label: "Ground transport arrangements" },
  { value: "Activities and Excursions", label: "Activities and excursions" },
  { value: "Restaurant Reservations", label: "Restaurant reservations" },
  { value: "Attendee Registration Management", label: "Attendee registration management" },
  { value: "Custom Landing Page", label: "Custom landing page" },
  { value: "Event Mobile App", label: "Event mobile app" },
] as const;

/** Built once at module load — every call site was independently rebuilding this. */
const ADDITIONAL_SERVICE_VALUES = new Set<string>(ADDITIONAL_SERVICE_OPTIONS.map((o) => o.value));

/**
 * Filters arbitrary input down to only recognized additional-service values. The single
 * allowlist enforcement point — server actions and jsonb reads both receive untrusted
 * input regardless of the TS signature, so this accepts `unknown` rather than `string[]`.
 *
 * @param value - Raw input (e.g. a server-action payload field or a jsonb column value).
 * @returns Only the entries that match `ADDITIONAL_SERVICE_OPTIONS`, in their original order.
 */
export function filterAdditionalServices(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string" && ADDITIONAL_SERVICE_VALUES.has(v));
}
